import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {FileRejection, useDropzone} from 'react-dropzone';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {fetchDocumentGenerateId} from 'api';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Icon} from 'components/ui-kit/Icon';
import {IconButton} from 'components/ui-kit/IconButton';
import {Input} from 'components/ui-kit/Input';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {isAdmin$, isNkRole$} from 'models/auth';
import {blockingForm$, blockingFormChanged, stepChanged} from 'models/documents/create';
import {colors} from 'theme';
import {BlokcingReason, BlockingFormData, BlockingScopes} from 'types/products-blocking';
import {mapEnumToI18nOptions} from 'utils/select';

import {FormContainer, FormField, FormWrapper, NavigationButtonsContainer} from '../common/styled';

import {
    AttachmentDropzone,
    AttachmentDropzoneAction,
    AttachmentDropzoneContent,
    AttachmentDropzoneHint,
    AttachmentDropzoneText,
    AttachmentFileIcon,
    AttachmentFileItem,
    AttachmentFileList,
    AttachmentFileMeta,
    AttachmentFileName,
    AttachmentFileSize,
    AttachmentFilesColumn,
    AttachmentFilesCount,
    AttachmentsContent,
    AttachmentsLabel,
    AttachmentsSection,
} from './styled';

const SUPPORTING_DOCUMENT_REQUIRED_REASON = BlokcingReason.REGULATORY_ACTION;
const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,.xlsx,.docx';
const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;
const FILE_SIZE_UNITS = ['B', 'KB', 'MB', 'GB'] as const;
const FILE_ICON_COLORS: Record<string, string> = {
    png: colors.brandPrimary,
    jpg: colors.brandPrimary,
    jpeg: colors.brandPrimary,
    xlsx: colors.success,
    xls: colors.success,
    pdf: colors.danger,
    docx: colors.brandDark,
    doc: colors.brandDark,
};

const schema = yup.object().shape({
    reason: yup.string().required(i18next.t('components.form.required')),
    scope: yup.string().required(i18next.t('components.form.required')),
    supportingDocumentNumber: yup
        .string()
        .trim()
        .when('reason', {
            is: SUPPORTING_DOCUMENT_REQUIRED_REASON,
            then: (value) => value.required(i18next.t('components.form.required')),
            otherwise: (value) => value.notRequired(),
        }),
    supportingDocumentDate: yup
        .date()
        .nullable()
        .when('reason', {
            is: SUPPORTING_DOCUMENT_REQUIRED_REASON,
            then: (value) => value.required(i18next.t('components.form.required')),
            otherwise: (value) => value.notRequired(),
        }),
    comment: yup.string().notRequired(),
});

const getError = (message?: string) =>
    message ? <span style={{fontSize: 12, color: 'red'}}>{i18next.t(message)}</span> : null;

const getRootFieldError = (error: unknown) => error as FieldError | undefined;

const formatFileSize = (bytes: number, locale: string) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return `0 ${FILE_SIZE_UNITS[0]}`;
    }

    if (bytes < 1024) {
        return `${bytes} ${FILE_SIZE_UNITS[0]}`;
    }

    const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        FILE_SIZE_UNITS.length - 1,
    );
    const value = bytes / 1024 ** unitIndex;

    return `${new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
    }).format(value)} ${FILE_SIZE_UNITS[unitIndex]}`;
};

const getFileExtension = (fileName: string) => {
    const extension = fileName.split('.').pop();

    return extension && extension !== fileName ? extension.toLowerCase() : '';
};

interface AttachmentsFieldProps {
    value: File[];
    onChange: (files: File[]) => void;
    onInvalid: () => void;
    onValid: () => void;
}

const AttachmentsField = ({value, onChange, onInvalid, onValid}: AttachmentsFieldProps) => {
    const {i18n, t} = useTranslation();
    const onDrop = useCallback(
        (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length) {
                onInvalid();
                return;
            }

            const nextFiles = [...value, ...acceptedFiles].filter(
                (file, index, files) =>
                    files.findIndex(
                        (candidate) =>
                            candidate.name === file.name &&
                            candidate.size === file.size &&
                            candidate.lastModified === file.lastModified,
                    ) === index,
            );

            if (nextFiles.length > MAX_ATTACHMENTS) {
                onInvalid();
                return;
            }

            onChange(nextFiles);
            onValid();
        },
        [onChange, onInvalid, onValid, value],
    );
    const {getInputProps, getRootProps, isDragActive} = useDropzone({
        accept: ATTACHMENT_ACCEPT,
        maxSize: MAX_ATTACHMENT_SIZE,
        multiple: true,
        onDrop,
    });

    return (
        <AttachmentsSection>
            <AttachmentsLabel>{t('pages.documents.form.blocking.attachments')}</AttachmentsLabel>
            <AttachmentsContent>
                <AttachmentDropzone isDragActive={isDragActive} {...getRootProps()}>
                    <input {...getInputProps()} />
                    <AttachmentDropzoneContent>
                        <Icon
                            source={UploadIcon}
                            width={24}
                            height={24}
                            fillColor={colors.brandPrimary}
                        />
                        <AttachmentDropzoneText>
                            {isDragActive ? (
                                t('pages.documents.form.blocking.dropAttachmentsHere')
                            ) : (
                                <>
                                    {t('pages.documents.form.blocking.dragAndDropAttachments')}{' '}
                                    <AttachmentDropzoneAction>
                                        {t('pages.documents.form.blocking.chooseFiles')}
                                    </AttachmentDropzoneAction>
                                </>
                            )}
                        </AttachmentDropzoneText>
                        <AttachmentDropzoneHint>
                            {t('pages.documents.form.blocking.attachmentsHint')}
                        </AttachmentDropzoneHint>
                    </AttachmentDropzoneContent>
                </AttachmentDropzone>
                <AttachmentFilesColumn>
                    <AttachmentFilesCount>
                        {t('pages.documents.form.blocking.filesAttached', {
                            attached: value.length,
                            max: MAX_ATTACHMENTS,
                        })}
                    </AttachmentFilesCount>
                    <AttachmentFileList>
                        {value.map((file) => {
                            const extension = getFileExtension(file.name);

                            return (
                                <AttachmentFileItem key={`${file.name}-${file.lastModified}`}>
                                    <AttachmentFileIcon
                                        background={
                                            FILE_ICON_COLORS[extension] || colors.textSecondary
                                        }
                                    >
                                        {extension || 'file'}
                                    </AttachmentFileIcon>
                                    <AttachmentFileMeta>
                                        <AttachmentFileName title={file.name}>
                                            {file.name}
                                        </AttachmentFileName>
                                        <AttachmentFileSize>
                                            {formatFileSize(file.size, i18n.language)}
                                        </AttachmentFileSize>
                                    </AttachmentFileMeta>
                                    <IconButton
                                        icon={
                                            <Icon
                                                source={CrossIcon}
                                                width={14}
                                                height={14}
                                                fillColor={colors.textPrimary}
                                            />
                                        }
                                        onClick={() => {
                                            onChange(value.filter((item) => item !== file));
                                            onValid();
                                        }}
                                    />
                                </AttachmentFileItem>
                            );
                        })}
                    </AttachmentFileList>
                </AttachmentFilesColumn>
            </AttachmentsContent>
        </AttachmentsSection>
    );
};

export const DocumentForm = () => {
    const isAdmin = useStore(isAdmin$);
    const isNkRole = useStore(isNkRole$);

    const {t} = useTranslation();
    const blockingForm = useStore(blockingForm$);
    const [isDocumentIdLoading, setDocumentIdLoading] = React.useState(false);
    const reasonOptions = useMemo(() => {
        const reasons = Object.entries(BlokcingReason).reduce((acc, [key, value]) => {
            const AUTOMATIC_REASONS = [BlokcingReason.RESERVED, BlokcingReason.EXPIRED];

            if (value === BlokcingReason.REGULATORY_ACTION) {
                return isAdmin || isNkRole ? {...acc, [key]: value} : acc;
            }

            return !AUTOMATIC_REASONS.includes(value) ? {...acc, [key]: value} : acc;
        }, {} as Record<string, BlokcingReason>);

        return mapEnumToI18nOptions(
            reasons,
            'dictionaries.products.blockingReason',
        ) as SelectOption<string>[];
    }, [t]);
    const scopeOptions = useMemo(
        () =>
            mapEnumToI18nOptions(
                BlockingScopes,
                'dictionaries.products.blockingScope',
            ) as SelectOption<string>[],
        [t],
    );
    const {
        clearErrors,
        control,
        handleSubmit,
        formState,
        setError,
        setValue,
        trigger,
        watch,
    } = useForm<BlockingFormData>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: blockingForm,
    });
    const reason = watch('reason');
    const isSupportingDocumentRequired = reason === SUPPORTING_DOCUMENT_REQUIRED_REASON;

    useEffect(() => {
        if (blockingForm.documentId) {
            setValue('documentId', blockingForm.documentId);
            return;
        }

        let cancelled = false;
        setDocumentIdLoading(true);
        fetchDocumentGenerateId()
            .then((documentId) => {
                if (!cancelled) setValue('documentId', documentId);
            })
            .catch((error) => console.error(error))
            .finally(() => {
                if (!cancelled) setDocumentIdLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [blockingForm.documentId, setValue]);

    const onReasonChange = (nextReason: string) => {
        setValue('reason', nextReason, {shouldValidate: true});
        trigger(['supportingDocumentDate', 'supportingDocumentNumber']);
    };

    const onHandleChanged = (data: BlockingFormData) => {
        blockingFormChanged(data);
        stepChanged(1);
    };

    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <FormWrapper>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="reason"
                                    render={({value}, {invalid}) => (
                                        <Select
                                            label={t('pages.documents.form.blocking.reason')}
                                            isRequired
                                            value={reasonOptions.find(
                                                (option) => option.value === value,
                                            )}
                                            options={reasonOptions}
                                            hasError={invalid}
                                            onChange={(option: SelectOption<string> | null) => {
                                                if (option?.value) onReasonChange(option.value);
                                            }}
                                        />
                                    )}
                                />
                                {getError(formState.errors.reason?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="scope"
                                    render={({value, onChange}, {invalid}) => (
                                        <Select
                                            label={t('pages.documents.form.blocking.scope')}
                                            isRequired
                                            value={scopeOptions.find(
                                                (option) => option.value === value,
                                            )}
                                            options={scopeOptions}
                                            hasError={invalid}
                                            onChange={(option: SelectOption<string> | null) =>
                                                onChange(option?.value ?? '')
                                            }
                                        />
                                    )}
                                />
                                {getError(formState.errors.scope?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="documentId"
                                    render={({value}) => (
                                        <Input
                                            label={t(
                                                'pages.documents.form.blocking.documentNumber',
                                            )}
                                            value={value ?? ''}
                                            disabled
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="comment"
                                    render={({value, onChange}) => (
                                        <Input
                                            label={t('pages.documents.form.blocking.comment')}
                                            placeholder={t(
                                                'pages.documents.form.blocking.commentPlaceholder',
                                            )}
                                            value={value ?? ''}
                                            onChange={onChange}
                                        />
                                    )}
                                />
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="supportingDocumentDate"
                                    render={({value, onChange}) => (
                                        <DatePicker
                                            label={t(
                                                'pages.documents.form.blocking.supportingDocumentDate',
                                            )}
                                            isRequired={isSupportingDocumentRequired}
                                            maxDate={new Date()}
                                            value={value}
                                            onChange={onChange}
                                            hasError={!!formState.errors.supportingDocumentDate}
                                        />
                                    )}
                                />
                                {getError(formState.errors.supportingDocumentDate?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="supportingDocumentNumber"
                                    render={({value, onChange}, {invalid}) => (
                                        <Input
                                            label={t(
                                                'pages.documents.form.blocking.supportingDocumentNumber',
                                            )}
                                            isRequired={isSupportingDocumentRequired}
                                            value={value ?? ''}
                                            onChange={onChange}
                                            hasError={invalid}
                                        />
                                    )}
                                />
                                {getError(formState.errors.supportingDocumentNumber?.message)}
                            </FormField>
                        </Grid>
                        <Grid item xs={12}>
                            <FormField>
                                <Controller
                                    control={control}
                                    name="attachmentFiles"
                                    render={({value = [], onChange}) => (
                                        <AttachmentsField
                                            value={value}
                                            onChange={onChange}
                                            onInvalid={() =>
                                                setError('attachmentFiles', {
                                                    message:
                                                        'pages.documents.form.blocking.invalidAttachments',
                                                })
                                            }
                                            onValid={() => clearErrors('attachmentFiles')}
                                        />
                                    )}
                                />
                                {getError(
                                    getRootFieldError(formState.errors.attachmentFiles)?.message,
                                )}
                            </FormField>
                        </Grid>
                    </Grid>
                </FormWrapper>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={
                            !formState.isValid || isDocumentIdLoading || formState.isSubmitting
                        }
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
